import { QueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { Button } from 'primereact/button';
import { ToastMessage } from 'primereact/toast';
import { Trans } from 'react-i18next';

import { IWebsocketMessage, ModuleType } from '../../contexts/websocket/WebsocketContext';
import DetailErrorMessageToastContent from '../../shared/components/detail-error-message-toast-content/DetailErrorMessageToastContent';
import { ToastSeverity } from '../../shared/enums/toast-severity';
import {
  getOptimisticCertificatePermissionKey,
  removeOptimisticCertificatePermission,
} from '../../shared/helpers/optimistic-certificate-permissions';
import {
  getOptimisticSupplierCertificateKey,
  removeOptimisticSupplierCertificate,
} from '../../shared/helpers/optimistic-supplier-certificates';
import { CertificatePermissionType } from '../../shared/interfaces/ICertificate';

// Backend message sent when a PCF is received via synchronous pull
const SYNC_PULL_SUCCESS_MESSAGE = 'REQUEST_PCF_SYNC_PULL_SUCCESS';

export enum InternalEvent {
  FILE_IMPORT_COMPLETE = 'fileImportComplete',
}

export interface MessageHandlerDependencies {
  queryClient: QueryClient;
  notify: (props: ToastMessage) => void;
  t: TFunction<'translation', undefined>;
}

export const createMessageHandlers = ({ queryClient, notify, t }: MessageHandlerDependencies) => {
  const requestPcfHandler = (message: IWebsocketMessage) => {
    queryClient.invalidateQueries({ queryKey: ['components'] });
    queryClient.invalidateQueries({ queryKey: ['requests/component-tasks'] });
    if (message.success === true && message.message === SYNC_PULL_SUCCESS_MESSAGE) {
      notify({
        severity: ToastSeverity.SUCCESS,
        summary: t('toastMessages.pcfRequest.syncSuccessSummary', {
          componentName: message.componentName,
        }),
        detail: t('toastMessages.pcfRequest.syncSuccessDetail', {
          componentName: message.componentName,
          supplierCompanyName: message.supplierCompanyName,
        }),
        life: 5000,
      });
    } else if (message.success === true) {
      notify({
        severity: ToastSeverity.SUCCESS,
        summary: t('toastMessages.pcfRequest.successSummary', {
          componentName: message.componentName,
        }),
        detail: t('toastMessages.pcfRequest.successDetail'),
        life: 5000,
      });
    } else if (message.success === false) {
      notify({
        severity: ToastSeverity.ERROR,
        summary: t('toastMessages.pcfRequest.errorSummary', {
          componentName: message.componentName,
        }),
        detail: t('toastMessages.pcfRequest.errorDetail'),
        life: 5000,
      });
    }
  };

  const requestBulkPcfSingleCompletedHandler = (message: IWebsocketMessage) => {
    const { success, component } = message;

    if (success && component) {
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['requests/component-tasks'] });
    }
  };

  const requestBulkPcfSummaryHandler = (message: IWebsocketMessage) => {
    queryClient.invalidateQueries({ queryKey: ['components'] });
    queryClient.invalidateQueries({ queryKey: ['requests/component-tasks'] });

    // Depending on the success and error count, show toast messages
    const { successCount, failureCount } = message;
    // Show success and error toasts based on the response
    if (successCount) {
      notify({
        severity: ToastSeverity.SUCCESS,
        detail: t('toastMessages.bulkRequestPcfs.states.success.detail', { successCount }),
      });
    }

    if (failureCount) {
      notify({
        severity: ToastSeverity.ERROR,
        detail: t('toastMessages.bulkRequestPcfs.states.failure.detail', { failureCount }),
      });
    }
  };

  const importFileHandler = (message: IWebsocketMessage) => {
    // Clean up import flag in session storage
    sessionStorage.removeItem('isImportingFile');

    // Dispatch event to notify other components about import completion
    window.dispatchEvent(new Event(InternalEvent.FILE_IMPORT_COMPLETE));

    if (message.type === ModuleType.IMPORT_PRODUCTS) {
      // Refresh products list
      queryClient.invalidateQueries({ queryKey: ['products'] });

      // Show toast messages based on import result
      if (message.success) {
        notify({
          summary: t('toastMessages.importProducts.states.success.title'),
          detail: t('toastMessages.importProducts.states.success.detail'),
          severity: ToastSeverity.SUCCESS,
          life: 5000,
        });
      } else {
        notify({
          content: (
            <DetailErrorMessageToastContent
              type='importProducts'
              detailErrorMessages={message.detailErrorMessages || []}
            />
          ),
          severity: ToastSeverity.WARN,
          sticky: true,
        });
      }
    } else if (message.type === ModuleType.ASSIGN_PRODUCTS) {
      // Refresh products & their permissions
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['permissions'] });

      if (message.success) {
        notify({
          summary: t('toastMessages.assignProducts.states.success.title'),
          detail: t('toastMessages.assignProducts.states.success.detail'),
          severity: ToastSeverity.SUCCESS,
          life: 5000,
        });
      } else {
        notify({
          content: (
            <DetailErrorMessageToastContent
              type='assignProducts'
              detailErrorMessages={message.detailErrorMessages || []}
            />
          ),
          severity: ToastSeverity.WARN,
          sticky: true,
        });
      }
    } else if (message.type === ModuleType.IMPORT_PRODUCTS_AND_PCFS) {
      // Refresh products & their permissions
      queryClient.invalidateQueries({ queryKey: ['products'] });

      if (message.success) {
        notify({
          summary: t('toastMessages.importProductPCFDialog.success.title'),
          detail: t('toastMessages.importProductPCFDialog.success.detail'),
          severity: ToastSeverity.SUCCESS,
          life: 5000,
        });
      } else if (message.success === false && message.errorReportUrl) {
        const errorReportUrl = message.errorReportUrl;
        notify({
          severity: ToastSeverity.ERROR,
          summary: t('toastMessages.importProductPCFDialog.failure.errorReport.title'),
          detail: (
            <div>
              <div>
                <Trans i18nKey='toastMessages.importProductPCFDialog.failure.errorReport.detail' />
              </div>
              <Button
                className='p-button-sm mt-2'
                label={t(
                  'toastMessages.importProductPCFDialog.failure.errorReport.downloadButtonLabel',
                )}
                severity='danger'
                onClick={() => {
                  // Download the error report file when clicked
                  // Create a temporary link element
                  const link = document.createElement('a');
                  link.href = errorReportUrl;
                  link.setAttribute('download', ''); // Suggest to the browser that this is a file to be downloaded rather than opened in the browser

                  // Append the link to the document body (this step is necessary for some browsers)
                  document.body.appendChild(link);

                  // Programmatically trigger a click event on the link
                  link.click();

                  // Clean up by removing the link element
                  document.body.removeChild(link);
                }}
              />
            </div>
          ),
          sticky: true,
        });
      }
    }
  };

  const calculationPdfGeneratedHandler = (message: IWebsocketMessage) => {
    if (message.success) {
      if (message.footprintId) {
        queryClient.invalidateQueries({ queryKey: ['footprints', 'id', message.footprintId] });
      }
    } else {
      notify({
        severity: ToastSeverity.ERROR,
        summary: t('toastMessages.calculationPdfGenerated.states.failure.title'),
        detail: t('toastMessages.calculationPdfGenerated.states.failure.detail'),
        life: 5000,
      });
    }
  };

  const requestUpdatedHandler = (message: IWebsocketMessage) => {
    const { failedCompanies } = message;

    if (message.success) {
      notify({
        severity: ToastSeverity.SUCCESS,
        detail: t('toastMessages.requestUpdated.states.success.detail'),
        life: 5000,
      });

      return;
    } else {
      notify({
        severity: ToastSeverity.ERROR,
        detail: (
          <div>
            <div>{t('toastMessages.requestUpdated.states.failure.intro')}</div>
            <ul className='mt-2 pl-3'>
              {failedCompanies?.map((companyName) => {
                return <li key={companyName}>{companyName}</li>;
              })}
            </ul>
            <div className='mt-2'>{t('toastMessages.requestUpdated.states.failure.footer')}</div>
          </div>
        ),
        sticky: true,
      });
    }
  };

  const certificateExchangeHandler = (message: IWebsocketMessage) => {
    // Handle pull certificate
    if (message.type === ModuleType.PULL_CERTIFICATES) {
      // Invalidate certificates list
      queryClient.invalidateQueries({ queryKey: ['certificate'] });

      // Invalidate certificate tasks list
      queryClient.invalidateQueries({ queryKey: ['certificate/tasks'] });

      // Invalidate supplier certificates list
      queryClient.invalidateQueries({ queryKey: ['certificate/supplier'] });

      // Reconcile the optimistic UI: the pull finished (success or failure), so drop only the
      // optimistic row for this certificate type + supplier. Other in-flight pulls keep their
      // placeholder until their own message arrives. The refetch above brings in the real data.
      removeOptimisticSupplierCertificate(
        queryClient,
        getOptimisticSupplierCertificateKey(message.certificateType, message.receiverCompanyName),
      );

      if (message.certificateId) {
        queryClient.invalidateQueries({
          queryKey: ['certificate', String(message.certificateId), 'permissions'],
        });

        // Same reconciliation for the permissions table of the certificate we granted access to:
        // the grant finished, so the refetch above replaces the placeholder row with the real one.
        if (message.receiverCompanyBpn) {
          removeOptimisticCertificatePermission(
            queryClient,
            getOptimisticCertificatePermissionKey(
              String(message.certificateId),
              message.receiverCompanyBpn,
              CertificatePermissionType.PULL,
            ),
          );
        }
      }

      let summary = '';
      let detail = '';

      if (!message.success) {
        if (
          message?.detailErrorMessage === 'CERTIFICATE_EXCHANGE_FAILED_TO_GET_EDC_URL_WITH_ASSET'
        ) {
          // Check if the error message is about failing to get edc url
          summary = t('toastMessages.grantAccessCertificateFinished.states.edcFailure.summary');
          detail = t('toastMessages.grantAccessCertificateFinished.states.edcFailure.detail', {
            companyName: message?.receiverCompanyName || 'Unknown',
          });
        } else {
          if (message?.message === 'CERTIFICATE_EXCHANGE_PULL_FAILED') {
            // Message for failed pull certificate
            detail = t('toastMessages.pullCertificateFinished.states.failure.detail');
          } else {
            // Message for failed grant access
            detail = t('toastMessages.grantAccessCertificateFinished.states.failure.detail');
          }
        }
      } else {
        if (message?.message === 'CERTIFICATE_EXCHANGE_PULL_SUCCESS') {
          // Message for successful pull certificate
          detail = t('toastMessages.pullCertificateFinished.states.success.detail', {
            certificateType: message.certificateType || t('global.notSpecified'),
            companyName: message.receiverCompanyName || t('global.notSpecified'),
          });
        } else {
          // Message for successful grant access
          detail = t('toastMessages.grantAccessCertificateFinished.states.success.detail', {
            companyName: message.receiverCompanyName || t('global.notSpecified'),
          });
        }
      }

      notify({
        severity: message.success ? ToastSeverity.SUCCESS : ToastSeverity.ERROR,
        summary,
        detail,
        life: message.success ? 5000 : undefined,
      });

      return;
    }

    // Handle push certificate
    if (message.type === ModuleType.PUSH_CERTIFICATES) {
      queryClient.invalidateQueries({ queryKey: ['certificate'] });

      if (message.certificateId) {
        // Refresh the permissions list on success, as the cert receiver will have new permissions
        // now — and on failure too, since a push that failed mid-exchange leaves a permission row
        // behind that the API then deletes.
        queryClient.invalidateQueries({
          queryKey: ['certificate', String(message.certificateId), 'permissions'],
        });

        // The push finished either way, so drop the placeholder row for this receiver. Other
        // in-flight shares keep theirs until their own message arrives.
        if (message.receiverCompanyBpn) {
          removeOptimisticCertificatePermission(
            queryClient,
            getOptimisticCertificatePermissionKey(
              String(message.certificateId),
              message.receiverCompanyBpn,
              CertificatePermissionType.PUSH,
            ),
          );
        }
      }

      let summary = '';
      let detail = '';

      if (!message.success) {
        if (
          message?.detailErrorMessage === 'CERTIFICATE_EXCHANGE_FAILED_TO_GET_EDC_URL_WITH_ASSET'
        ) {
          // Check if the error message is about failing to get edc url
          summary = t('toastMessages.shareCertificateFinished.states.edcFailure.summary');
          detail = t('toastMessages.shareCertificateFinished.states.edcFailure.detail', {
            companyName: message?.receiverCompanyName || 'Unknown',
          });
        } else {
          detail = t('toastMessages.shareCertificateFinished.states.failure.detail');
        }
      } else {
        detail = t('toastMessages.shareCertificateFinished.states.success.detail');
      }

      notify({
        severity: message.success ? ToastSeverity.SUCCESS : ToastSeverity.ERROR,
        summary,
        detail: detail,
        life: message.success ? 5000 : undefined,
      });

      return;
    }

    // Handle request certificate
    if (message.type === ModuleType.REQUEST_CERTIFICATE) {
      notify({
        severity: ToastSeverity.SUCCESS,
        detail: t('toastMessages.requestCertificateSent.detail', {
          receiverCompanyName: message.receiverCompanyName || t('global.notSpecified'),
        }),
        life: 5000,
      });

      return;
    }

    // Handle review certificate
    if (message.type === ModuleType.REVIEW_CERTIFICATES) {
      // Refresh supplier certificate list and the details of the certificate if currently viewing
      queryClient.invalidateQueries({ queryKey: ['certificate/supplier'] });
      queryClient.invalidateQueries({
        queryKey: ['certificate', message.certificateId?.toString()],
      });

      notify({
        severity: message.success ? ToastSeverity.SUCCESS : ToastSeverity.ERROR,
        detail: message.success
          ? t('toastMessages.supplierCertificateReviewFinished.states.success.detail')
          : t('toastMessages.supplierCertificateReviewFinished.states.failure.detail'),
        life: message.success ? 5000 : undefined,
      });

      return;
    }
  };

  return {
    requestPcfHandler,
    requestBulkPcfSingleCompletedHandler,
    requestBulkPcfSummaryHandler,
    importFileHandler,
    calculationPdfGeneratedHandler,
    requestUpdatedHandler,
    certificateExchangeHandler,
  };
};
